import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FiSettings, FiChevronDown, FiEye, FiEyeOff, FiColumns } from 'react-icons/fi';
import { TagRead, TagType } from '../../schemas';
import { useTagContext } from '../../contexts/tag_context';
import { useAIStateContext } from '../../contexts/ai_chat_state_context';
import { PillToggle } from '../SharedComponents/PillToggle';

interface DisplaySettingsDropdownProps {
    /** Whether to filter requests by current chat */
    filterByCurrentChat: boolean;
    /** Callback to change the chat filter */
    setFilterByCurrentChat: (value: boolean) => void;
    /** Set of tag IDs that have explanation columns shown */
    explanationColumnIds: Set<string>;
    /** Callback to toggle explanation column for a tag */
    toggleExplanationColumn: (tagId: string) => void;
}

/**
 * A dropdown that shows display settings for the document grid,
 * including visibility toggles and explanation column controls.
 */
export const DisplaySettingsDropdown: React.FC<DisplaySettingsDropdownProps> = ({
    filterByCurrentChat,
    setFilterByCurrentChat,
    explanationColumnIds,
    toggleExplanationColumn,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { tagMap, codeTags, transcriptTags, visibleTagIds, setVisibleTagIds, toggleTagVisibility } = useTagContext()!;
    const { currentChat } = useAIStateContext();

    // Close dropdown on outside click or Escape
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    // Get all request tags (filtered by current chat if toggle is on)
    const requestTags = useMemo(() => {
        return Array.from(tagMap.values())
            .filter(tag => {
                if (tag.type !== TagType.REQUEST_SET) return false;
                if (filterByCurrentChat && currentChat) {
                    return tag.chat_id === currentChat.id;
                }
                return true;
            })
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [tagMap, filterByCurrentChat, currentChat]);

    // Check if a tag is visible
    const isTagVisible = useCallback((tagId: string) => {
        if (visibleTagIds === null) return true;
        return visibleTagIds.has(tagId);
    }, [visibleTagIds]);

    // Toggle all codes visibility
    const toggleAllCodes = useCallback((visible: boolean) => {
        setVisibleTagIds((prev) => {
            const codeIds = codeTags.map(t => t.id);
            if (visible) {
                const next = new Set(prev ?? []);
                codeIds.forEach(id => next.add(id));
                return next.size > 0 ? next : null;
            } else {
                if (prev === null) {
                    const next = new Set(Array.from(tagMap.keys()));
                    codeIds.forEach(id => next.delete(id));
                    return next;
                }
                const next = new Set(prev);
                codeIds.forEach(id => next.delete(id));
                return next;
            }
        });
    }, [codeTags, tagMap, setVisibleTagIds]);

    // Toggle all request tags visibility
    const toggleAllRequests = useCallback((visible: boolean) => {
        setVisibleTagIds((prev) => {
            const requestIds = requestTags.map(t => t.id);
            if (visible) {
                const next = new Set(prev ?? []);
                requestIds.forEach(id => next.add(id));
                return next.size > 0 ? next : null;
            } else {
                if (prev === null) {
                    const next = new Set(Array.from(tagMap.keys()));
                    requestIds.forEach(id => next.delete(id));
                    return next;
                }
                const next = new Set(prev);
                requestIds.forEach(id => next.delete(id));
                return next;
            }
        });
    }, [requestTags, tagMap, setVisibleTagIds]);

    // Toggle all transcript tags visibility
    const toggleAllTranscript = useCallback((visible: boolean) => {
        setVisibleTagIds((prev) => {
            const transcriptIds = transcriptTags.map(t => t.id);
            if (visible) {
                const next = new Set(prev ?? []);
                transcriptIds.forEach(id => next.add(id));
                return next.size > 0 ? next : null;
            } else {
                if (prev === null) {
                    const next = new Set(Array.from(tagMap.keys()));
                    transcriptIds.forEach(id => next.delete(id));
                    return next;
                }
                const next = new Set(prev);
                transcriptIds.forEach(id => next.delete(id));
                return next;
            }
        });
    }, [transcriptTags, tagMap, setVisibleTagIds]);

    // Counts
    const visibleCodesCount = useMemo(() => {
        return codeTags.filter(t => isTagVisible(t.id)).length;
    }, [codeTags, isTagVisible]);

    const visibleTranscriptCount = useMemo(() => {
        return transcriptTags.filter(t => isTagVisible(t.id)).length;
    }, [transcriptTags, isTagVisible]);

    const visibleRequestsCount = useMemo(() => {
        return requestTags.filter(t => isTagVisible(t.id)).length;
    }, [requestTags, isTagVisible]);

    const currentChatRequestCount = useMemo(() => {
        if (!currentChat) return 0;
        return Array.from(tagMap.values()).filter(
            tag => tag.type === TagType.REQUEST_SET && tag.chat_id === currentChat.id
        ).length;
    }, [tagMap, currentChat]);

    const totalRequestCount = useMemo(() => {
        return Array.from(tagMap.values()).filter(
            tag => tag.type === TagType.REQUEST_SET
        ).length;
    }, [tagMap]);

    const TagRow: React.FC<{ tag: TagRead }> = ({ tag }) => {
        const visible = isTagVisible(tag.id);
        const hasExplanationCol = explanationColumnIds.has(tag.id);

        return (
            <div className={`flex items-center gap-2 px-2 py-1.5 rounded ${visible ? 'bg-white' : 'bg-[#ececf3] opacity-60'} border border-[#a1a1b6]`}>
                <button
                    onClick={() => toggleTagVisibility(tag.id)}
                    className={`p-1 rounded transition-colors ${visible ? 'text-emerald-600 hover:bg-emerald-50' : 'text-[#6d6d8a] hover:bg-[#ececf3]'}`}
                    title={visible ? 'Hide from grid' : 'Show in grid'}
                >
                    {visible ? <FiEye className="w-[0.875em] h-[0.875em]" /> : <FiEyeOff className="w-[0.875em] h-[0.875em]" />}
                </button>

                <span className={`flex-1 text-sm truncate ${visible ? 'text-[#1c1c24]' : 'text-[#6d6d8a]'}`} title={tag.tag_name}>
                    {tag.tag_name}
                </span>

                <button
                    onClick={() => toggleExplanationColumn(tag.id)}
                    className={`p-1 rounded transition-colors ${hasExplanationCol ? 'text-[#8c8ca3] bg-[#ececf3] ring-1 ring-[#8c8ca3]' : 'text-[#6d6d8a] hover:bg-[#ececf3]'}`}
                    title={hasExplanationCol ? 'Hide explanation column' : 'Show explanation column'}
                >
                    <FiColumns className="w-[0.875em] h-[0.875em]" />
                </button>
            </div>
        );
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                className="flex items-center gap-1.5 rounded-xl border border-[#a1a1b6] px-3 py-1 text-sm text-[#1c1c24] hover:bg-[#ececf3] transition-colors"
                onClick={() => setIsOpen(!isOpen)}
            >
                <FiSettings className="text-[#6d6d8a] w-[1em] h-[1em]" />
                <span>Display</span>
                <FiChevronDown className={`text-[#6d6d8a] transition-transform w-[0.875em] h-[0.875em] ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Content */}
            {isOpen && (
                <div className="absolute right-0 top-full z-30 mt-1 w-80 rounded-xl border border-[#a1a1b6] bg-white shadow-lg">
                    {/* Header */}
                    <div className="px-3 py-2 border-b border-[#a1a1b6] bg-[#ececf3] rounded-t-xl">
                        <h3 className="text-sm font-semibold text-[#1c1c24]">Display Settings</h3>
                        <p className="text-xs text-[#6d6d8a] mt-0.5">Control what shows in grid cells</p>
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        {/* Document Requests Section */}
                        <div className="px-3 py-2 border-b border-[#a1a1b6]">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-[#6d6d8a]">
                                    Document Requests
                                </span>
                                <span className="text-xs text-[#6d6d8a]">
                                    {visibleRequestsCount}/{requestTags.length} visible
                                </span>
                            </div>

                            {/* Chat filter toggle */}
                            <div className="mb-2">
                                <PillToggle
                                    options={[
                                        {
                                            value: true,
                                            label: currentChat ? `Chat (${currentChatRequestCount})` : 'Chat',
                                            disabled: !currentChat,
                                            title: !currentChat
                                                ? "Open a chat to filter"
                                                : `${currentChatRequestCount} request${currentChatRequestCount !== 1 ? 's' : ''} in chat`,
                                        },
                                        {
                                            value: false,
                                            label: `All (${totalRequestCount})`,
                                            title: `${totalRequestCount} total request${totalRequestCount !== 1 ? 's' : ''}`,
                                        },
                                    ]}
                                    value={currentChat ? filterByCurrentChat : false}
                                    onChange={setFilterByCurrentChat}
                                    showLabels={true}
                                    size="xs"
                                />
                            </div>

                            {/* Toggle all buttons */}
                            <div className="flex gap-2 mb-2">
                                <button
                                    onClick={() => toggleAllRequests(true)}
                                    className="text-xs text-[#8c8ca3] hover:text-[#6d6d8a]"
                                >
                                    Show all
                                </button>
                                <span className="text-[#a1a1b6]">|</span>
                                <button
                                    onClick={() => toggleAllRequests(false)}
                                    className="text-xs text-[#6d6d8a] hover:text-[#1c1c24]"
                                >
                                    Hide all
                                </button>
                            </div>

                            {/* Request tags list */}
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                                {requestTags.length === 0 ? (
                                    <div className="text-xs text-[#6d6d8a] italic py-2">
                                        {filterByCurrentChat && currentChat
                                            ? 'No requests in current chat'
                                            : 'No document requests available'}
                                    </div>
                                ) : (
                                    requestTags.map(tag => <TagRow key={tag.id} tag={tag} />)
                                )}
                            </div>
                        </div>

                        {/* Codes Section */}
                        <div className="px-3 py-2 border-b border-[#a1a1b6]">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-[#6d6d8a]">
                                    Codes
                                </span>
                                <span className="text-xs text-[#6d6d8a]">
                                    {visibleCodesCount}/{codeTags.length} visible
                                </span>
                            </div>

                            {/* Toggle all buttons */}
                            <div className="flex gap-2 mb-2">
                                <button
                                    onClick={() => toggleAllCodes(true)}
                                    className="text-xs text-[#8c8ca3] hover:text-[#6d6d8a]"
                                >
                                    Show all
                                </button>
                                <span className="text-[#a1a1b6]">|</span>
                                <button
                                    onClick={() => toggleAllCodes(false)}
                                    className="text-xs text-[#6d6d8a] hover:text-[#1c1c24]"
                                >
                                    Hide all
                                </button>
                            </div>

                            {/* Code tags list */}
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                                {codeTags.length === 0 ? (
                                    <div className="text-xs text-[#6d6d8a] italic py-2">
                                        No codes available
                                    </div>
                                ) : (
                                    codeTags.map(tag => <TagRow key={tag.id} tag={tag} />)
                                )}
                            </div>
                        </div>

                        {/* Testimony Section (transcript exchange tags) */}
                        {transcriptTags.length > 0 && (
                            <div className="px-3 py-2">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-[#6d6d8a]">
                                        Testimony
                                    </span>
                                    <span className="text-xs text-[#6d6d8a]">
                                        {visibleTranscriptCount}/{transcriptTags.length} visible
                                    </span>
                                </div>

                                <div className="flex gap-2 mb-2">
                                    <button
                                        onClick={() => toggleAllTranscript(true)}
                                        className="text-xs text-[#8c8ca3] hover:text-[#6d6d8a]"
                                    >
                                        Show all
                                    </button>
                                    <span className="text-[#a1a1b6]">|</span>
                                    <button
                                        onClick={() => toggleAllTranscript(false)}
                                        className="text-xs text-[#6d6d8a] hover:text-[#1c1c24]"
                                    >
                                        Hide all
                                    </button>
                                </div>

                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {transcriptTags.map(tag => <TagRow key={tag.id} tag={tag} />)}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer info */}
                    <div className="px-3 py-2 border-t border-[#a1a1b6] bg-[#ececf3] rounded-b-xl">
                        <div className="text-xs text-[#6d6d8a]">
                            <FiEye className="w-[0.75em] h-[0.75em] inline mr-1" /> = shows in grid cells
                        </div>
                        <div className="text-xs text-[#6d6d8a] mt-0.5">
                            <FiColumns className="w-[0.75em] h-[0.75em] inline mr-1" /> = adds explanation column
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DisplaySettingsDropdown;
